import produce from 'immer';
import { uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import { RECOMMEND_STORE_TYPE } from '../../../../constants/service/app/recommendStoreConstants';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../../lib/common';
import { _LIST_PAGINATION_MODEL } from '../../app/models';
import actions from './actions';
import {
  RECOMMEND_STORE_SAVE_MODEL,
  RECOMMEND_STORE_LIST_SEARCH_DATA_MODEL,
  RECOMMEND_STORE_LIST_SEARCH_OPTION_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  추천 브랜드 리스트
   */
  recommendStoreList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: RECOMMEND_STORE_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: RECOMMEND_STORE_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  추천 브랜드 상세
   */
  recommendStoreDetail: {
    /**
     *  추천 브랜드 정보
     */
    recommendStoreInfo: {
      /**
       *  추천 브랜드 식별 id
       */
      recommendStoreId: null,
      /**
       *  추천인
       */
      adminName: null,
      /**
       *  추천 브랜드 타입
       */
      type: RECOMMEND_STORE_TYPE.SEARCH_RECOMMEND,
      /**
       *  추천 브랜드 생성일
       */
      createDate: null,
      /**
       *  메모
       */
      memo: null,
    },
    /**
     *  추천 브랜드 아이템 리스트
     */
    recommendStoreItemList: [],
  },

  /**
   *  추천 브랜드 등록/복사
   */
  recommendStoreSave: RECOMMEND_STORE_SAVE_MODEL,
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  추천 브랜드 데이터 변경하기 성공
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  추천 브랜드 리스트 가져오기 성공
     */
    [actions.GET_RECOMMEND_STORE_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendStoreList = {
          ...draft.recommendStoreList,
          ...action.payload.recommendStoreList,
        };
      });
    },

    /**
     *  추천 브랜드 상세 요청하기 성공
     */
    [actions.GET_RECOMMEND_STORE_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendStoreDetail = action.payload.recommendStoreDetail;
      });
    },

    /**
     *  추천 브랜드 복사하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_COPY_RECOMMEND_STORE_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 브랜드 리스트
         */
        draft.recommendStoreSave.recommendStoreItemList.itemList = {
          ...draft.recommendStoreSave.recommendStoreItemList.itemList,
          ...action.payload.recommendStoreSave.recommendStoreItemList,
        };

        /**
         *  브랜드 정보
         */
        draft.recommendStoreSave.recommendStoreInfo = action.payload.recommendStoreSave.recommendStoreInfo;

        /**
         *  선택된 브랜드 리스트
         */
        draft.recommendStoreSave.recommendStoreItemList.selectedItemList = {
          list: action.payload.recommendStoreSave.selectedRecommendStoreItemList,
          idList: action.payload.recommendStoreSave.selectedRecommendStoreItemList.map((item) => item.id),
        };

        /**
         *  선택할 브랜드 아이템 중에 이미 설정된 브랜드 아이템인 경우에는
         *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendStoreSave.recommendStoreItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendStoreSave.recommendStoreItemList.itemList.list,
          selectedIdList: draft.recommendStoreSave.recommendStoreItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  추천 브랜드 등록하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_CREATE_RECOMMEND_STORE_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 브랜드 리스트
         */
        draft.recommendStoreSave.recommendStoreItemList.itemList =
          action.payload.recommendStoreSave.recommendStoreItemList;
      });
    },

    /**
     *  추천 브랜드에 연결할 아이템 리스트 요청하기 성공
     */
    [actions.GET_RECOMMEND_STORE_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 브랜드 리스트
         */
        draft.recommendStoreSave.recommendStoreItemList.itemList = {
          ...draft.recommendStoreSave.recommendStoreItemList.itemList,
          ...action.payload.recommendStoreItemList,
        };

        /**
         *  선택할 브랜드 아이템 중에 이미 설정된 브랜드 아이템인 경우에는
         *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendStoreSave.recommendStoreItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendStoreSave.recommendStoreItemList.itemList.list,
          selectedIdList: draft.recommendStoreSave.recommendStoreItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  브랜드 리스트 EXCEL 업로드 성공
     */
    [actions.SET_RECOMMEND_STORE_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 브랜드 리스트
         */
        draft.recommendStoreSave.recommendStoreItemList.selectedItemList.list = action.payload.itemList;

        /**
         *  서브 텍스트 필드 동기화
         */
        action.payload.itemList.forEach((item) => {
          action.payload.form.setFieldsValue({ [`subText_${item.id}`]: item.subText });
          action.payload.form.validateFields({ [`subText_${item.id}`]: item.subText });
        });

        /**
         *  선택된 브랜드 아이템의 중복 제거하기
         */
        draft.recommendStoreSave.recommendStoreItemList.selectedItemList.list = uniqBy(
          draft.recommendStoreSave.recommendStoreItemList.selectedItemList.list,
          'id',
        );

        /**
         *  선택할 브랜드 아이템 중에 이미 설정된 브랜드 아이템인 경우에는
         *  브랜드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendStoreSave.recommendStoreItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendStoreSave.recommendStoreItemList.itemList.list,
          selectedIdList: draft.recommendStoreSave.recommendStoreItemList.selectedItemList.list.map((item) => item.id),
        });
      });
    },
  },
  initialState,
);
